import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useReactiveVar } from '@apollo/client';

import { useBind, useSnackbar, usePhoneNumberValidation, useCancellablePromise, useSmartCaptcha } from '../../../../../../hooks';
import { PhoneStepForm } from './phone-step-form';
import { yup } from '../../../../../../services/yup';
import { ITokenData, oauthApi } from '../../../../../../rest/oauth';
import {
  useActionsInProgress,
} from '../../../../../../graphql/preloader/actions/actions-in-progress';
import { IExternalStateProps, IStepsState, TPhoneFormValues } from '../../registration.models';
import { TimerStatusOptions } from '../../../reset-password/reset-password.models';
import { authErrors, setPhoneStepValues } from '../../../../../../graphql/user/user.cache';
import { CAPTCHA_CHECK_ERROR, PHONE_LENGTH } from '../../../../../../constants';
import { RecaptchaActions } from '../../../../../../rest/consumer';
import { useRegistrationError } from '../../hooks/use-registration-error';
import {
  IFormImperativeHandleProps,
  IStepComponentProps,
  IStepperImperativeHandleProps,
  SmartCaptcha,
} from '../../../../../common';
import { CaptchaType } from '../../../../../../graphql/configuration';
import { handleReCaptcha } from '../../../../../../utils';

export const PhoneStep = forwardRef<IStepperImperativeHandleProps, IStepComponentProps>(({
  stepsState,
  onGoNextSuccess,
  onGoNextFail,
  onGoNext,
  isNavigationAllowed,
  externalState,
  onNextButtonDisabled,
}, ref): JSX.Element => {
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const stepsStateRegistration = stepsState as IStepsState;
  const {
    makeCancellablePromise,
    CancelledPromiseOnUnmountError,
  } = useCancellablePromise();

  const { enqueueSnackbar } = useSnackbar();
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();

  const phoneNumberValidation = usePhoneNumberValidation();

  const validationSchema = useMemo(() => yup.object({
    phoneNumber: phoneNumberValidation,
  }), [phoneNumberValidation]);

  useImperativeHandle(ref, () => ({
    goNext() {
      formRef.current!.submit();
    },
    goBack() {
      return {
        formValues: formRef.current!.getValues(),
      };
    },
  }));

  const defaultValues = useMemo(
    () => stepsStateRegistration.phone && stepsStateRegistration.phone.formValues,
    [stepsStateRegistration.phone],
  );

  const makeCancellablePromiseBind = useBind(makeCancellablePromise);
  const CancelledPromiseOnUnmountErrorBind = useBind(CancelledPromiseOnUnmountError);
  const onGoNextSuccessBind = useBind(onGoNextSuccess);
  const onGoNextFailBind = useBind(onGoNextFail);
  const onGoNextBind = useBind(onGoNext);
  const enqueueSnackbarBind = useBind(enqueueSnackbar);
  const isNavigationAllowedBind = useBind(isNavigationAllowed);
  const addActionInProgressBind = useBind(addActionInProgress);
  const removeActionInProgressBind = useBind(removeActionInProgress);

  const { start, reset, status: timerStatus, captcha } = externalState as IExternalStateProps;

  const [isNewNumber, setIsNewNumber] = useState<boolean>(false);

  const disableSubmitButton = () => {
    if (formRef?.current?.getValues()) {
      const { phoneNumber: phoneFieldValue } = formRef.current.getValues();
      const isSubmitDisabled = (phoneFieldValue as string).length !== PHONE_LENGTH;

      onNextButtonDisabled(isSubmitDisabled);
    }
  };

  const phoneStepValues = useReactiveVar(setPhoneStepValues);

  const handleNoSubmit = useCallback((formValues: TPhoneFormValues): void => {
    onGoNextBind.current();
    onGoNextSuccessBind.current({
      ...phoneStepValues,
      formValues,
    });
  }, []);

  const handleAfterSubmit = (formValues: TPhoneFormValues, token: ITokenData) => {
    setPhoneStepValues({
      formValues,
      token,
    });

    reset();
    start();

    removeActionInProgressBind.current();

    onGoNextSuccessBind.current({
      formValues,
      token,
    });
  };

  const { handleError } = useRegistrationError();

  // SmartCaptcha
  const { smartToken, isVisible, handleSmartCaptcha, showCaptcha, clearToken, isReset, handleReset, hideCaptcha } = useSmartCaptcha();

  // This function is called when the SmartCaptcha challenge is hidden (either passed or closed)
  const handleChallengeHidden = useCallback((): void => {
    if (!smartToken) {
      clearToken();
      hideCaptcha();
    }
  }, [smartToken]);

  const handlePhoneChange = useCallback(() => {
    // Clear the captcha token when phone number changes
    // User needs to complete captcha again for the new phone number
    if (smartToken && captcha?.type === CaptchaType.smartCaptcha && !captcha.isVisibleCaptcha) {
      clearToken();
    }

    if (formRef?.current?.getValues() && timerStatus === TimerStatusOptions.running) {
      const { phoneNumber: phoneFieldNewValue } = formRef.current.getValues();
      const phone = stepsStateRegistration?.phone.formValues.phoneNumber;

      if (phoneFieldNewValue !== phone) {
        setIsNewNumber(true);
      }

      if (phoneFieldNewValue === phone) {
        setIsNewNumber(false);
      }
    }

    disableSubmitButton();
  }, [setIsNewNumber, formRef, stepsStateRegistration?.phone, smartToken, captcha, clearToken]);

  const handlePhoneSubmit = useCallback(
    async (formValues: TPhoneFormValues, captchaToken: string, captchaType?: CaptchaType) => {
      const { data: token } = await makeCancellablePromiseBind.current(
        oauthApi.registerPhoneNumberCaptcha({
          phoneNumber: formValues.phoneNumber,
        }, captchaToken, captchaType),
      );

      handleAfterSubmit(formValues, token);
      authErrors({ phoneNumberExists: false });
    }, [makeCancellablePromiseBind, handleAfterSubmit, authErrors]);

  const handleSubmitError = useCallback((error: any): void => {
    removeActionInProgressBind.current();

    if (error instanceof CancelledPromiseOnUnmountErrorBind.current) {
      return;
    }

    handleError(error);
    onGoNextFailBind.current();
  }, [removeActionInProgressBind, handleError, onGoNextFailBind, CancelledPromiseOnUnmountErrorBind]);

  const handleSubmit = useCallback(async (formValues: TPhoneFormValues) => {
    if (!isNavigationAllowedBind.current) {
      return;
    }

    // Smart Captcha - Invisible mode
    if (captcha && captcha?.isEnabled && captcha.type === CaptchaType.smartCaptcha && !captcha.isVisibleCaptcha) {
      showCaptcha(async (token: string) => {
        onGoNextBind.current();
        addActionInProgressBind.current();

        try {
          await handlePhoneSubmit(formValues, token, captcha.type);
          clearToken();
          handleReset(true);
        } catch (error: any) {
          handleSubmitError(error);
          handleReset(true);
        }
      });

      return;
    }

    // Smart Captcha - Visible mode
    if (captcha && captcha?.isEnabled && captcha.type === CaptchaType.smartCaptcha && captcha.isVisibleCaptcha) {
      if (!smartToken) {
        enqueueSnackbarBind.current(CAPTCHA_CHECK_ERROR, { variant: 'error' });
        return;
      }

      onGoNextBind.current();
      addActionInProgressBind.current();

      try {
        await handlePhoneSubmit(formValues, smartToken || '', captcha.type);
        clearToken();
        handleReset(true);
      } catch (error: any) {
        handleSubmitError(error);
        handleReset(true);
      }

      return;
    }

    // For non-SmartCaptcha flows, proceed normally
    onGoNextBind.current();
    addActionInProgressBind.current();

    try {
      if (captcha && captcha?.isEnabled && captcha.type === CaptchaType.reCaptcha) {
        handleReCaptcha(
          (recaptchaToken: string) => handlePhoneSubmit(formValues, recaptchaToken, captcha.type),
          handleSubmitError,
          RecaptchaActions.registration,
        );
      } else {
        const { data: token } = await makeCancellablePromiseBind.current(
          oauthApi.registerPhoneNumber({
            phoneNumber: formValues.phoneNumber,
          }),
        );

        handleAfterSubmit(formValues, token);
        authErrors({ phoneNumberExists: false });
      }
    } catch (error: any) {
      handleSubmitError(error);
    }
  }, [
    reset,
    start,
    timerStatus,
    onGoNextBind,
    onGoNextSuccessBind,
    isNavigationAllowedBind,
    enqueueSnackbarBind,
    makeCancellablePromiseBind,
    addActionInProgressBind,
    captcha,
    handlePhoneSubmit,
    showCaptcha,
    handleSubmitError,
    smartToken,
    clearToken,
    handleReCaptcha,
    handleReset,
  ]);

  useEffect(() => {
    disableSubmitButton();
  }, []);

  return (
    <>
      <PhoneStepForm
        ref={formRef}
        onSubmit={(timerStatus === TimerStatusOptions.running) && !isNewNumber ? handleNoSubmit : handleSubmit}
        validationSchema={validationSchema}
        defaultValues={defaultValues}
        onPhoneChange={handlePhoneChange}
      />
      {(captcha?.type === CaptchaType.smartCaptcha && !(timerStatus === TimerStatusOptions.running && !isNewNumber)) && (
        <SmartCaptcha
          onSuccess={handleSmartCaptcha}
          isVisible={isVisible}
          isVisibleCaptcha={!!captcha?.isVisibleCaptcha}
          isReset={isReset}
          onChallengeHidden={handleChallengeHidden}
        />
      )}
    </>
  );
});
